// src/pages/Admin/Coaching/EditHoursModal.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import Button from "../../../components/Button/Button";
import { FaSave, FaTimes } from "react-icons/fa";
import { saveCoachHour } from "../../../services/eventServices";
import { toast } from "react-toastify";

export default function EditHoursModal({ isOpen, onClose, event, refreshEvents }) {
  const [coachHour, setCoachHour] = useState(0);
  const [saving, setSaving] = useState(false);

  // Libellés utiles
  const { dateLabel, titleLabel } = useMemo(() => {
    const dateLabel = event?.start
      ? new Date(event.start).toLocaleDateString("fr-FR", { dateStyle: "medium" })
      : "—";
    const titleLabel = event?.titre || "Événement";
    return { dateLabel, titleLabel };
  }, [event]);

  // Synchroniser la valeur à chaque ouverture / changement d'event
  useEffect(() => {
    if (isOpen && event) {
      const initial = Number(event?.AdherentEvent?.coach_hour || 0);
      setCoachHour(Number.isFinite(initial) ? initial : 0);
      setSaving(false);
    }
  }, [isOpen, event]);

  // Fermer avec ESC
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose]);

  const handleBackdropClick = useCallback(
    (e) => {
      if (e.target === e.currentTarget) onClose?.();
    },
    [onClose]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!event?.AdherentEvent) return;

    const value = Number(coachHour);
    if (!Number.isFinite(value) || value < 0) {
      toast.error("Veuillez saisir un nombre valide (≥ 0).");
      return;
    }

    try {
      setSaving(true);
      await saveCoachHour(
        event.AdherentEvent.adherent_id,
        event.AdherentEvent.event_id,
        { coach_hour: value }
      );
      toast.success("Heures de coaching mises à jour avec succès.");
      onClose?.();
      refreshEvents?.();
    } catch (error) {
      console.error("Erreur lors de la mise à jour des heures de coaching:", error);
      toast.error("Erreur lors de la mise à jour des heures de coaching.");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onMouseDown={handleBackdropClick}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" />

      {/* Modal */}
      <div
        className="relative w-full max-w-md rounded-2xl shadow-2xl overflow-hidden"
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-coach-hours-title"
      >
        {/* Header gradient */}
        <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4 flex items-center justify-between">
          <h3
            id="edit-coach-hours-title"
            className="text-white text-lg font-bold uppercase tracking-wide"
          >
            Modifier les Heures de Coaching
          </h3>
          <button
            onClick={onClose}
            className="inline-flex items-center justify-center h-9 w-9 rounded-xl bg-white/15 text-white hover:bg-white/25 transition"
            aria-label="Fermer"
          >
            <FaTimes />
          </button>
        </div>

        {/* Body */}
        <div className="bg-white dark:bg-slate-900 p-5 max-h-[70vh] overflow-y-auto">
          {/* Contexte de l’événement */}
          <div className="mb-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-900 px-4 py-3">
            <div className="text-sm text-slate-500 dark:text-slate-400">Événement</div>
            <div className="text-slate-800 dark:text-slate-100 font-semibold">{titleLabel}</div>
            <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">Date : {dateLabel}</div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">
                Heures de présence
              </label>
              <input
                type="number"
                inputMode="decimal"
                step="0.25"
                min="0"
                value={coachHour}
                onChange={(e) => setCoachHour(e.target.value)}
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                placeholder="Ex. 1.5"
                required
              />
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                Saisissez un nombre d’heures (ex. 1, 1.5, 2.25). Valeurs par pas de 0.25.
              </p>
            </div>

            <div className="flex items-center justify-end gap-2 pt-2">
              <Button
                text="Annuler"
                onClick={onClose}
                icon={FaTimes}
                type="button"
                className="!bg-slate-100 !text-slate-800 hover:!bg-slate-200 dark:!bg-slate-800 dark:!text-slate-100 dark:hover:!bg-slate-700 !rounded-xl gap-2"
              />
              <Button
                text={saving ? "Enregistrement..." : "Enregistrer"}
                icon={FaSave}
                type="submit"
                className="!rounded-xl gap-2"
                disabled={saving}
              />
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
